import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Calendar, Info, Mail, ArrowLeft, Mountain, Search } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const links = [
    { to: '/',             icon: Home,     label: 'Homepage' },
    { to: '/reservation',  icon: Calendar, label: 'Reservations' },
    { to: '/about',        icon: Info,     label: 'About Us' },
    { to: '/contact',      icon: Mail,     label: 'Contact' },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center px-4 hp2s">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Inter:wght@300;400;500&display=swap');
          .hp2s,.hp2s .bg-black,.hp2s .bg-gray-900{background-color:#0D0D0D!important}
          .hp2s .bg-gray-900\/95,.hp2s .bg-gray-900\/80{background-color:rgba(13,13,13,.95)!important}
          .hp2s .bg-gray-800,.hp2s .bg-gray-800\/60,.hp2s .bg-gray-800\/80{background-color:rgba(255,255,255,.025)!important}
          .hp2s .bg-gray-900\/50{background-color:rgba(13,13,13,.5)!important}
          .hp2s [class*="from-amber-5"]{background:#C4A25A!important;background-image:none!important}
          .hp2s [class*="from-amber-5"]:hover:not(:disabled){background:#D4B870!important}
          .hp2s [class*="from-yellow-4"]{background:#C4A25A!important;background-image:none!important}
          .hp2s .text-white{color:#E2D0B0!important}
          .hp2s .text-gray-300{color:#C8B48E!important}
          .hp2s .text-gray-400{color:#6B6B6B!important}
          .hp2s .text-gray-500{color:rgba(107,107,107,.65)!important}
          .hp2s .text-gray-600{color:rgba(107,107,107,.5)!important}
          .hp2s .text-gray-900{color:#0D0D0D!important}
          .hp2s .text-amber-500{color:#C4A25A!important}
          .hp2s .text-amber-400{color:#D4B870!important}
          .hp2s .text-amber-300{color:#E0C880!important}
          .hp2s .text-emerald-400,.hp2s .text-emerald-300{color:#86c88a!important}
          .hp2s .text-red-400{color:#e07070!important}
          .hp2s .border-gray-700{border-color:rgba(196,162,90,.18)!important}
          .hp2s .border-gray-800{border-color:rgba(196,162,90,.1)!important}
          .hp2s .border-amber-500,.hp2s .border-amber-500\/60{border-color:#C4A25A!important}
          .hp2s .border-amber-500\/30{border-color:rgba(196,162,90,.3)!important}
          .hp2s .border-amber-500\/40{border-color:rgba(196,162,90,.4)!important}
          .hp2s .hover\:border-amber-500\/40:hover{border-color:rgba(196,162,90,.4)!important}
          .hp2s .bg-amber-500{background-color:#C4A25A!important}
          .hp2s .bg-amber-500\/10,.hp2s .bg-amber-700\/15{background-color:rgba(196,162,90,.08)!important}
          .hp2s .bg-amber-500\/15{background-color:rgba(196,162,90,.12)!important}
          .hp2s .hover\:bg-amber-500:hover{background-color:#C4A25A!important}
          .hp2s .hover\:text-amber-500:hover{color:#C4A25A!important}
          .hp2s .hover\:text-gray-900:hover{color:#0D0D0D!important}
          .hp2s .font-serif,.hp2s h1,.hp2s h2,.hp2s h3,.hp2s h4{font-family:'Cormorant Garamond',Georgia,serif!important;font-weight:300!important}
          .hp2s .rounded-2xl,.hp2s .rounded-xl,.hp2s .rounded-lg,.hp2s .rounded-full{border-radius:2px!important}
          .hp2s .rounded-md,.hp2s .rounded{border-radius:1px!important}
          .hp2s input,.hp2s textarea,.hp2s select{background-color:rgba(255,255,255,.03)!important;color:#E2D0B0!important}
          .hp2s input:focus,.hp2s textarea:focus,.hp2s select:focus{border-color:rgba(196,162,90,.7)!important}
          .hp2s option{background-color:#141414!important;color:#E2D0B0!important}
          .hp2s .bg-emerald-500\/15{background-color:rgba(134,200,138,.1)!important}
          .hp2s .border-emerald-500\/30{border-color:rgba(134,200,138,.3)!important}
          .hp2s .bg-red-500\/15{background-color:rgba(224,112,112,.1)!important}
          .hp2s .border-red-500\/30{border-color:rgba(224,112,112,.3)!important}
          .hp2s .tracking-\[0\.3em\]{color:#C4A25A!important;font-size:.68rem!important}
          .hp2s iframe{border:1px solid rgba(196,162,90,.25)!important}
        `}</style>
      {/* Glow blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
      </div>

      <div className="relative max-w-lg w-full text-center space-y-10">

        {/* Logo */}
        <Link to="/" className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 transition-colors">
          <Mountain className="w-7 h-7" />
          <span className="font-serif text-xl font-bold tracking-wide">Swat Capital Resort</span>
        </Link>

        {/* 404 */}
        <div className="relative select-none">
          <p className="text-[9rem] font-extrabold leading-none text-gray-800">404</p>
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-[9rem] font-extrabold leading-none text-transparent bg-clip-text bg-gradient-to-b from-amber-400/40 to-amber-600/10">
              404
            </p>
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <h1 className="font-serif text-3xl font-bold text-white">Page Not Found</h1>
          <p className="text-gray-400">
            The page you're looking for doesn't exist or has been moved.
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search menu, gallery, info..."
              className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500/70 focus:ring-2 focus:ring-amber-500/20 transition-all"
            />
          </div>
          <button
            type="submit"
            className="px-5 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-gray-900 font-bold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all"
          >
            Search
          </button>
        </form>

        {/* Quick links */}
        <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-5 grid grid-cols-2 gap-3">
          {links.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-gray-300 hover:text-amber-400 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/20 transition-all text-sm font-medium"
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </Link>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 justify-center">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-6 py-3 rounded-xl border border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white transition-all font-medium"
          >
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>
          <Link
            to="/"
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-gray-900 font-bold hover:from-amber-600 hover:to-amber-700 transition-all"
          >
            <Home className="w-4 h-4" /> Homepage
          </Link>
        </div>

      </div>
    </div>
  );
};

export default NotFound;
